import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Modal,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

import {
  EmptyState,
  ErrorAlert,
  Loading,
  Meter,
  PageHeader,
  UpgradeGrid,
} from "../../layout/secure/common";
import SurveyTable from "../../layout/secure/survey-table";
import {
  currentUsage,
  listSurveys,
  messageFromError,
  runSurveyAction,
} from "../../utils/api";

const STATUS_OPTIONS = [
  ["", "Current"],
  ["draft", "Draft"],
  ["published", "Published"],
  ["closed", "Closed"],
  ["archived", "Archived"],
  ["deleted", "Deleted"],
];

function Surveys() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [surveyData, usageData] = await Promise.all([
        listSurveys({
          status: status || undefined,
          search: query || undefined,
          page,
          limit: 25,
          includeDeleted: status === "deleted" || undefined,
        }),
        currentUsage(),
      ]);
      setData(surveyData);
      setUsage(usageData);
    } catch (requestError) {
      setError(messageFromError(requestError, "Surveys could not be loaded."));
    }
  }, [page, query, status]);

  useEffect(() => { load(); }, [load]);

  const perform = async (item, action) => {
    if (["archive", "delete", "restore"].includes(action)) {
      setConfirmation({ item, action });
      return;
    }
    setBusyId(item.uuid);
    try {
      await runSurveyAction(item.uuid, action);
      toast.success(action === "publish"
        ? (item.status === "closed" ? "Survey reopened." : "Survey published.")
        : `Survey ${action}d.`);
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, `The survey could not be ${action}d.`));
    } finally {
      setBusyId(null);
    }
  };

  const confirmAction = async () => {
    const { item, action } = confirmation;
    setConfirmation(null);
    setBusyId(item.uuid);
    try {
      await runSurveyAction(item.uuid, action);
      toast.success(action === "delete"
        ? "Survey moved to deleted."
        : `Survey ${action}d.`);
      await load();
    } catch (requestError) {
      setError(messageFromError(requestError, `The survey could not be ${action}d.`));
    } finally {
      setBusyId(null);
    }
  };

  const submitSearch = event => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  if (!data && !error) return <Loading label="Loading surveys" />;
  const items = data?.items || [];
  const surveys = usage?.surveys || {};
  const activeFull = surveys.remaining === 0;

  return (
    <>
      <PageHeader
        title="Surveys"
        description="Create drafts freely, then publish within your active-survey allowance."
        action={<Button as={Link} to="/dashboard/surveys/new">New survey</Button>}
      />
      <ErrorAlert error={error} onRetry={load} />
      {activeFull ? (
        <Alert variant="warning">
          Your active-survey allowance is full. Drafts remain editable; close an active survey or upgrade before publishing another.
        </Alert>
      ) : null}
      <section className="fera-panel">
        <div className="fera-survey-toolbar">
          <div className="fera-compact-meter">
            <Meter
              label="Active surveys"
              value={surveys.active || data?.counts?.active || 0}
              maximum={surveys.activeLimit}
            />
          </div>
          <Form className="fera-search-form" onSubmit={submitSearch}>
            <Form.Label className="visually-hidden" htmlFor="survey-search">Search surveys</Form.Label>
            <Form.Control
              id="survey-search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search surveys"
            />
            <Button type="submit" variant="outline-primary">Search</Button>
          </Form>
        </div>
        <div className="fera-filter-tabs" role="group" aria-label="Filter surveys by status">
          {STATUS_OPTIONS.map(([value, label]) => (
            <Button
              key={label}
              size="sm"
              variant={status === value ? "primary" : "outline-secondary"}
              onClick={() => {
                setStatus(value);
                setPage(1);
              }}
            >
              {label}
            </Button>
          ))}
        </div>
        {!items.length ? (
          <EmptyState
            title={query || status ? "No matching surveys" : "No surveys to show"}
            detail={query || status
              ? "Try another search or status filter."
              : "Create a draft survey. Active capacity is checked only when you publish or reopen it."}
            action={!query && !status
              ? <Button as={Link} to="/dashboard/surveys/new">Create survey</Button>
              : null}
          />
        ) : (
          <SurveyTable items={items} busyId={busyId} onAction={perform} />
        )}
        {(data?.pagination?.pages || 1) > 1 ? (
          <Pagination className="fera-pagination">
            <Pagination.Prev
              disabled={page <= 1}
              onClick={() => setPage(value => Math.max(value - 1, 1))}
            />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next
              disabled={page >= data.pagination.pages}
              onClick={() => setPage(value => value + 1)}
            />
          </Pagination>
        ) : null}
      </section>
      {activeFull ? <UpgradeGrid access={usage?.access} /> : null}
      <Modal
        show={Boolean(confirmation)}
        onHide={() => setConfirmation(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmation?.action === "delete" ? "Delete survey?" : `${confirmation?.action} survey?`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmation?.action === "delete"
            ? "The survey link will be disabled and new invitations will stop. Historical responses and reports are preserved."
            : `This will ${confirmation?.action} “${confirmation?.item?.name}”.`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmation(null)}>Cancel</Button>
          <Button
            variant={confirmation?.action === "delete" ? "danger" : "primary"}
            onClick={confirmAction}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Surveys;
